import { Container, Wizard } from '@valid-eval/shared-react-components';
import { useTranslation } from 'react-i18next';

import Loading from 'components/Loading';
import { useFetchCommunications } from 'data/features/communicationsHooks';
import { useEditEventContents } from 'data/features/contentsHooks';
import { useFetchDocuments } from 'data/features/documentsHooks';
import { useFetchEvent, useFetchEventUsers } from 'data/features/eventsHooks';
import { useFetchPhases } from 'data/features/phasesHooks';
import withRouter, { WithRouterProps } from 'routes/withRouter';
import ErrorBoundary from 'screens/ErrorBoundary';

import useEditEventStepsState from './components/EditEventSteps/hooks/useEditEventStepsState';

type EditEventProps = WithRouterProps;

const EditEvent = ({ params }: EditEventProps) => {
  const eventId = String(params.event_id);
  const { t } = useTranslation();

  useFetchCommunications(eventId);
  const { event } = useFetchEvent(eventId);
  const { phases } = useFetchPhases(eventId);
  const { contents } = useEditEventContents(eventId);
  const { navigators, organizers } = useFetchEventUsers(eventId);
  const { documents } = useFetchDocuments(eventId);

  const { activeKey, setActiveKey, steps, progress, nextStep } = useEditEventStepsState({
    event,
    phases,
    contents,
    navigators,
    organizers,
    documents,
  });

  return !event ? (
    <Loading />
  ) : (
    <ErrorBoundary>
      <Container className={'full-height-container'}>
        <Wizard
          nav={
            <>
              <Wizard.NavTittle>
                {t('event.form.setup_title', { name: event.name })}
              </Wizard.NavTittle>
              <Wizard.NavProgressBar progress={progress} />
              <Wizard.Nav items={steps} />
            </>
          }
          activeKey={activeKey}
          setActiveKey={setActiveKey}
          unmountOnExit
          mountOnEnter
        >
          {steps.map((step) => (
            <Wizard.Pane key={step.eventKey} eventKey={step.eventKey}>
              <step.component
                event={event}
                label={step.label}
                subLabel={step.subLabel}
                step={step}
                nextStep={nextStep}
              />
            </Wizard.Pane>
          ))}
        </Wizard>
      </Container>
    </ErrorBoundary>
  );
};

export default withRouter(EditEvent);
